import { currentUser } from "@/App";
import ArtistAccountTabs from "@/components/ArtistAccountTabs";
import UserProfileDrawer from "@/components/UserProfileDrawer";
import PageAppBar from "@/pages/ArtistAdmin/PageAppBar";
import { updateUser } from "@/services/api";
import { useCurrentUser, useMutedComments } from "@/services/hooks";
import { formatSeconds } from "@/utils";
import { formatDate } from "@/utils/time";
import {
  Avatar,
  Box,
  Container,
  Divider,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useRef, useState } from "react";

export default function Moderation() {
  const { data: mutedComments = [], isLoading } = useMutedComments(
    currentUser?.key
  );

  const { data: user } = useCurrentUser();
  const queryClient = useQueryClient();
  const [communityStandards, setCommunityStandards] = useState("");
  const hasHydrated = useRef(false);

  useEffect(() => {
    if (user && !hasHydrated.current) {
      setCommunityStandards(user.meta?.community_standards ?? "");
      hasHydrated.current = true;
    }
  }, [user]);

  const standardsMutation = useMutation({
    mutationFn: (value) =>
      updateUser(null, {
        community_standards: value,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["current_user"], data);
    },
  });

  const debouncedStandardsSave = useCallback(
    debounce((value) => {
      standardsMutation.mutate(value);
    }, 1000),
    []
  );

  const handleStandardsChange = (e) => {
    const value = e.target.value;
    setCommunityStandards(value);
    debouncedStandardsSave(value);
  };

  return (
    <Container maxWidth="sm" sx={{ py: 10 }}>
      <PageAppBar />
      <ArtistAccountTabs />

      <TextField
        label="Community Standards"
        value={communityStandards}
        onChange={handleStandardsChange}
        multiline
        minRows={3}
        fullWidth
        helperText={
          standardsMutation.isPending
            ? "Saving..."
            : "Used by AI Moderator to mute comments that are out of line."
        }
      />

      <Divider sx={{ my: 5 }} />

      <Box sx={{ mb: 3 }}>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ letterSpacing: 1 }}
        >
          Muted comments
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Comments muted by the AI moderator across your content.
        </Typography>
      </Box>

      {isLoading && <LoadingState />}

      {!isLoading && mutedComments.length === 0 && (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography variant="body2" color="text.secondary">
            No muted comments.
          </Typography>
        </Box>
      )}

      {!isLoading && mutedComments.length > 0 && (
        <Box>
          {mutedComments.map((event) => (
            <CommentRow key={event.id} event={event} />
          ))}
        </Box>
      )}
    </Container>
  );
}

function LoadingState() {
  return (
    <Stack spacing={2}>
      {[0, 1, 2, 3].map((i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          height={80}
          sx={{ borderRadius: "10px" }}
        />
      ))}
    </Stack>
  );
}

function CommentRow({ event }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ opacity: 0.5, mb: 0.5 }}
      >
        <Avatar
          src={event.content?.owner?.meta?.profile}
          sx={{ width: 18, height: 18 }}
        />
        <Typography variant="body2">{event.content?.owner?.name}</Typography>
        <Typography variant="caption" color="darkgray">
          {event.content?.type}
        </Typography>
      </Stack>

      {event.content?.meta?.text && (
        <Typography
          variant="body1"
          sx={{ ml: 3.2, fontSize: "0.8em", opacity: 0.5, mb: 1 }}
        >
          {event.content.meta.text}
        </Typography>
      )}

      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={1}
        sx={{ ml: 3.2 }}
      >
        <UserProfileDrawer
          userKey={event.user?.key}
          userProfile={event.user?.meta?.profile}
          avatarSize={24}
        />
        <Box sx={{ width: "100%" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {event.user?.name}
            </Typography>
            <Typography variant="caption" sx={{ color: "lightgray" }}>
              {formatDate(event.created)}
            </Typography>
            {event.data?.current_time > 0 && (
              <Typography variant="caption" sx={{ color: "lightgray" }}>
                (at {formatSeconds(event.data.current_time)})
              </Typography>
            )}
          </Stack>
          <Box sx={{ wordWrap: "break-word", whiteSpace: "pre-wrap" }}>
            <Typography variant="body1" sx={{ fontSize: "0.9em" }}>
              {event.data?.text}
            </Typography>
            {event.data?.mute_reason && (
              <Typography
                variant="caption"
                sx={{ color: "warning.main", display: "block", mt: 0.5 }}
              >
                Muted: {event.data.mute_reason}
              </Typography>
            )}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}
